import { defineMessages, type IntlShape } from 'react-intl';

const GIST_WORD_LIMIT = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

const messages = defineMessages({
  today: { defaultMessage: 'Today, {time}', id: '4aaopOD5m5' },
  tomorrow: { defaultMessage: 'Tomorrow, {time}', id: 'OytUQDVqwy' },
  dayTime: { defaultMessage: '{day}, {time}', id: 'ZX+9wc0qYq' },
  lastSeen: { defaultMessage: 'Last seen {relative}', id: 'hM/UFiQ4PC' },
});

export const clampObjectGist = (gist: string | null | undefined): string | undefined => {
  const words = gist?.trim().split(/\s+/).filter(Boolean);
  return words?.length ? words.slice(0, GIST_WORD_LIMIT).join(' ') : undefined;
};

const startOfDay = (value: Date): number =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();

export const formatSessionWhen = (
  intl: IntlShape,
  date: Date,
  now: Date = new Date()
): string => {
  const time = intl.formatTime(date, { hour: 'numeric', minute: '2-digit' });
  const dayOffset = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
  if (dayOffset === 0) return intl.formatMessage(messages.today, { time });
  if (dayOffset === 1) return intl.formatMessage(messages.tomorrow, { time });
  const day = intl.formatDate(date, { month: 'short', day: 'numeric' });
  return intl.formatMessage(messages.dayTime, { day, time });
};

/** Day, week, month and year buckets, like the patient search row's "Saw {relative}". */
export const formatLastSeen = (
  intl: IntlShape,
  date: Date,
  now: Date = new Date()
): string | undefined => {
  const days = Math.floor((now.getTime() - date.getTime()) / DAY_MS);
  if (Number.isNaN(days) || days < 0) return undefined;
  const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
    days < 7
      ? [days, 'day']
      : days < 30
        ? [Math.floor(days / 7), 'week']
        : days < 365
          ? [Math.floor(days / 30), 'month']
          : [Math.floor(days / 365), 'year'];
  const relative = intl.formatRelativeTime(-value, unit, { numeric: 'auto' });
  return intl.formatMessage(messages.lastSeen, { relative });
};

const SEX_INITIAL: Record<string, string> = { female: 'F', male: 'M' };

/** PHI — render-only. */
export const patientDemographicsLabel = (
  age: number | null,
  gender: string | null | undefined,
  dob: string | null
): string | undefined => {
  const sexAge = `${age ?? ''}${SEX_INITIAL[gender?.trim().toLowerCase() ?? ''] ?? ''}`;
  return [sexAge, dob].filter(Boolean).join(' · ') || undefined;
};
